/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { FC, useEffect } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  RouteObject,
  useLocation,
} from 'react-router-dom';
import { useSetSavedURLParams } from '../hooks';
import {
  adminPathPrefix,
  appPathPrefix,
  authPathPrefix,
  getPageTitle,
  pages,
  paths,
} from '../pages';
import { NotFound } from '../pages/public';
import { Wrapper } from '../styled';
import { developmentMode } from '../utils/consts';
import { ReactGA } from '../utils/google-analytics';
import {
  isAdminPath,
  isAppPath,
  isAuthPath,
  isInnerAppPath,
} from '../utils/helpers';
import { AppAndAdminRoutesWrapper } from '../components/AppAndAdminRoutesWrapper';
import { AuthRoute } from '../components/AuthRoute';
import { PrivateRoute } from '../components/PrivateRoute';
import { RootProviders } from './RootProviders';
import { useAuthContext } from './Auth';

type Page = keyof typeof pages;

const VerifyPage = pages.verify;

const notFoundRoute: RouteObject = {
  path: '*',
  element: <NotFound />,
};

const commonRoutes: RouteObject[] = Object.entries(paths)
  .filter(([, path]) => !isAuthPath(path) && !isInnerAppPath(path))
  .map(([name, path]) => {
    const Page = pages[name as Page];
    return {
      path,
      element: <Page />,
    };
  });

const authChildren: RouteObject[] = [
  ...Object.entries(paths)
    .filter(([, path]) => isAuthPath(path))
    .map(([name, path]) => {
      const Page = pages[name as Page];
      return {
        path: path.replace(`${authPathPrefix}/`, ''),
        element: <Page />,
      };
    }),
  notFoundRoute,
];

const appChildren: RouteObject[] = [
  ...Object.entries(paths)
    .filter(([, path]) => path !== `${appPathPrefix}/verify` && isAppPath(path))
    .map(([name, path]) => {
      const Page = pages[name as Page];
      return {
        path: path.replace(`${appPathPrefix}/`, ''),
        element: <Page />,
      };
    }),
  notFoundRoute,
];

const adminChildren: RouteObject[] = [
  ...Object.entries(paths)
    .filter(([, path]) => isAdminPath(path))
    .map(([name, path]) => {
      const Page = pages[name as Page];
      return {
        path: path.replace(`${adminPathPrefix}/`, ''),
        element: <Page />,
      };
    }),
  notFoundRoute,
];

const RootRedirect = () => {
  const authorized = useAuthContext();
  return <Navigate to={authorized ? paths.dashboard : paths.login} replace />;
};

const RoutingEffects = () => {
  const location = useLocation();

  useSetSavedURLParams();

  useEffect(() => {
    document.title = getPageTitle(location.pathname);
    if (!developmentMode) {
      ReactGA.pageview(location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  return null;
};

const RootLayout = () => (
  <RootProviders>
    <RoutingEffects />
    <Wrapper>
      <Outlet />
    </Wrapper>
  </RootProviders>
);

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      {
        path: authPathPrefix,
        element: <Navigate to={paths.login} replace />,
      },
      {
        path: appPathPrefix,
        element: <Navigate to={paths.dashboard} replace />,
      },
      {
        path: adminPathPrefix,
        element: <Navigate to={paths.adminAllUsers} replace />,
      },
      ...commonRoutes,
      {
        path: `${authPathPrefix}/*`,
        element: <AuthRoute />,
        children: authChildren,
      },
      {
        path: `${appPathPrefix}/verify`,
        element: (
          <PrivateRoute>
            <VerifyPage />
          </PrivateRoute>
        ),
      },
      {
        path: `${appPathPrefix}/*`,
        element: <AppAndAdminRoutesWrapper />,
        children: appChildren,
      },
      {
        path: `${adminPathPrefix}/*`,
        element: <AppAndAdminRoutesWrapper />,
        children: adminChildren,
      },
      notFoundRoute,
    ],
  },
]);

const Router: FC = () => <RouterProvider router={router} />;

export { Router };
